import type { PayloadAction } from "@reduxjs/toolkit"
import { createAppSlice } from "../../app/createAppSlice"
import type { GameLogicCharacter } from "@types"

// Load default from JSON
export const characterInitialState: GameLogicCharacter = {
  name: "Your Name Here",
  avatar: {
    skinLayer: "/character/skin-medium.png",
    eyebrowsLayer: "/character/eyebrows-neutral.png",
    hairLayer: "/character/hair-messy-locs.png",
    glassesLayer: "/character/glasses-reading.png",
    faceLayer: "/character/face-neutral.png",
    chestLayer: "/character/outfit-formal-pants.png",
    shoesLayer: "/character/shoes-sneakers.png",
  },
}

// Canonical render/control order for the customizable layers. Renderers must
// iterate THIS list — never the keys of a persisted avatar object, so stale
// keys from an old session (e.g. the retired outlineLayer) can't re-enter the
// composite. The fixed outline/eyes overlays are hardcoded in the renderers.
export const AVATAR_LAYER_ORDER = [
  "skinLayer",
  "eyebrowsLayer",
  "hairLayer",
  "glassesLayer",
  "faceLayer",
  "chestLayer",
  "shoesLayer",
] as const satisfies readonly (keyof GameLogicCharacter["avatar"])[]

export const AvatarOptionsData = {
  skinLayer: [
    "/character/skin-light.png",
    "/character/skin-medium.png",
    "/character/skin-dark.png",
  ],
  hairLayer: [
    "/character/hair-messy-locs.png",
    "/character/hair-short-fro.png",
    "/character/hair-side-braids.png",
    "/character/hair-curly-headband.png",
    "/character/hair-pigtail-braids.png",
    "/character/hair-afro-puffs.png",
    "/character/defaultEmpty.svg",
  ],
  eyebrowsLayer: [
    "/character/eyebrows-neutral.png",
    "/character/eyebrows-quizzical.png",
    "/character/defaultEmpty.svg",
  ],
  glassesLayer: [
    "/character/glasses-reading.png",
    "/character/defaultEmpty.svg",
  ],
  faceLayer: ["/character/face-neutral.png", "/character/face-smile.png"],
  chestLayer: [
    "/character/outfit-formal-pants.png",
    "/character/outfit-formal-skirt.png",
    "/character/outfit-jean-skirt.png",
    "/character/outfit-long-skirt.png",
    "/character/outfit-sweat-pants.png",
  ],
  shoesLayer: [
    "/character/shoes-sneakers.png",
    "/character/shoes-sandals.png",
    "/character/shoes-formal.png",
  ],
}

export const characterSlice = createAppSlice({
  name: "character",
  initialState: characterInitialState,
  reducers: create => ({
    updateCharacterAvatar: create.reducer(
      (state, action: PayloadAction<GameLogicCharacter["avatar"]>) => {
        state.avatar = action.payload
      },
    ),
    updateCharacterName: create.reducer(
      (state, action: PayloadAction<string>) => {
        state.name = action.payload
      },
    ),
  }),

  // You can define your selectors here. These selectors receive the slice
  // state as their first argument.
  selectors: {
    // State
    selectCharacterAvatar: character => character.avatar,
    selectCharacterName: character => character.name,
  },
})

// Action creators are generated for each case reducer function.
export const { updateCharacterAvatar, updateCharacterName } =
  characterSlice.actions

// Selectors regameed by `slice.selectors` take the root state as their first argument.
export const { selectCharacterAvatar, selectCharacterName } =
  characterSlice.selectors
